import { useMemo } from "react";

import { SearchDataMissingError } from "@/errors/SearchDataMissingError";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import {
  TripType,
  type FlightOfferFlight,
} from "@/features/flights/types/types";

/**
 * The reason why this hook exists when we have `useSelectedOffer` is for 3 main reasons:
 * - To avoid making unnecessary API calls when we already have the selected offer data in the context
 * - To expose the selected flight data in a more convenient way
 * - To have the same typed offer as in context
 */
export function useSelectedFlightOffer() {
  const { searchData } = useFlightSearchDataContext();
  if (!searchData)
    throw new SearchDataMissingError("Flights search data is missing");

  const isRoundTrip = searchData.tripType === TripType.ROUND_TRIP;

  const { selectedFlightPayload: offer } = useFlightSelectionPayloadContext();
  if (!offer) throw new Error("No selected offer found");

  const { departureFlight, arrivalFlight } = useMemo<{
    departureFlight: FlightOfferFlight;
    arrivalFlight: FlightOfferFlight | null;
  }>(() => {
    const departureFlight = offer.flights[0] ?? null;
    const arrivalFlight = offer.flights[1] ?? null;

    if (!departureFlight)
      throw new Error("No departure flight found in the selected offer");
    if (isRoundTrip && !arrivalFlight)
      throw new Error("No arrival flight found in the selected offer");

    return { departureFlight, arrivalFlight };
  }, [isRoundTrip, offer.flights]);

  return {
    isRoundTrip,
    offer,
    departureFlight,
    arrivalFlight,
  };
}
